import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getPendingFanPosts,
  getFanPostImages,
  approveFanPost,
  rejectFanPost,
  deleteFanPost,
  getFanImagePublicUrl,
  type FanPost,
} from '../../services/api/fanwall';

export default function FanWallManagerPage() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<FanPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [imagesByPost, setImagesByPost] = useState<Record<string, string[]>>({});

  function load() {
    getPendingFanPosts()
      .then(async (list) => {
        setPosts(list);
        const map: Record<string, string[]> = {};
        await Promise.all(
          list.map(async (p) => {
            const imgs = await getFanPostImages(p.id);
            map[p.id] = imgs.map((i) => getFanImagePublicUrl(i.storage_path));
          })
        );
        setImagesByPost(map);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  async function handleApprove(id: string) {
    await approveFanPost(id);
    load();
  }

  async function handleReject(id: string) {
    await rejectFanPost(id);
    load();
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this post permanently?')) return;
    await deleteFanPost(id);
    load();
  }

  if (loading) return <p className="text-[var(--color-text-muted)]">{t('common.loading')}</p>;

  return (
    <div className="admin-page">
      <h1 className="admin-page__title">{t('admin.fanwallModeration')}</h1>
      <p className="mb-6 text-sm text-[var(--color-text-muted)]">{t('admin.fanwallHint')}</p>
      <div className="admin-fanwall-list">
        {posts.length === 0 && <p className="admin-page__empty">{t('admin.noPendingPosts')}</p>}
        {posts.map((p) => (
          <div key={p.id} className="admin-fanwall-card">
            {p.author_name && <p className="admin-fanwall-card__author">{p.author_name}</p>}
            <p className="admin-fanwall-card__message">{p.message}</p>
            <p className="admin-fanwall-card__date">{new Date(p.created_at).toLocaleString()}</p>
            {imagesByPost[p.id]?.length > 0 && (
              <div className="admin-fanwall-card__images">
                {imagesByPost[p.id].map((url, i) => (
                  <img key={i} src={url} alt="" className="admin-fanwall-card__img" />
                ))}
              </div>
            )}
            <div className="admin-fanwall-card__actions">
              <button type="button" onClick={() => handleApprove(p.id)} className="admin-btn admin-btn--sm admin-btn--success">{t('admin.approve')}</button>
              <button type="button" onClick={() => handleReject(p.id)} className="admin-btn admin-btn--sm admin-btn--warn">{t('admin.reject')}</button>
              <button type="button" onClick={() => handleDelete(p.id)} className="admin-btn admin-btn--sm admin-btn--danger">{t('common.delete')}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
